import { KeyboardArrowDown } from "@mui/icons-material";
import { Box, Button, FormLabel, Stack } from "@mui/joy";
import { format } from "date-fns";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";

import { useRoutesUiStore } from "@/components/RoutesProvider";
import { getCachedCompetition } from "@/hooks/competitions";
import { useOnUserChange } from "@/hooks/useOnUserChange";

import { Route } from "../../core/types";
import {
  useCurrentVenueCircuitLookup,
  useCurrentVenueCompetitionLookup,
  useVenueLinesByUuid,
  useVenueRoutesQuery,
  useVenueZonesByClimbType,
} from "../../hooks/routes";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "../../hooks/venues";
import CheckboxInput from "../forms/CheckboxInput";
import CircuitSelect from "../forms/CircuitSelect";
import { ColorsFormInput } from "../forms/ColorsSelect";
import { DifficultyInput } from "../forms/DifficultyInput";
import FormGroup from "../forms/FormGroup";
import { GradePickerFormInput } from "../forms/GradePicker";
import LineSelect from "../forms/LineSelect";
import MapPositionEditInput from "../forms/MapPositionEditInput";
import { NumberSelectInput } from "../forms/NumberSelectInput";
import { PillsSelectInput } from "../forms/PillsSelect";
import RouteLabelsInput from "../forms/RouteLabelsInput";
import StaffSelect from "../forms/StaffSelect";
import { TagScanInput } from "../forms/TagInput";
import TextInput, {
  DateInput,
  HeightInputAutoUnits,
  NumberInput,
  TextareaInput,
} from "../forms/TextInput";
import { ToggleButtonSelectInput } from "../forms/ToggleButtonSelect";
import { TraitSelectorFormInput } from "../forms/TraitSelector";
import { RouteFormValues } from "./formValues";

type SectionProps = { children: ReactNode };

export const FormSection = ({ children }: SectionProps) => (
  <Box sx={{ paddingX: 2, paddingY: 1 }}>{children}</Box>
);

export const CompetitionCategoriesSelect = ({
  competitionUuid,
  name,
}: {
  competitionUuid: string;
  name?: string;
}) => {
  const competition = useCurrentVenueCompetitionLookup(competitionUuid);

  if (competition?.autoRouteGroupMode === "SINGLE") {
    return null;
  }

  return (
    <ToggleButtonSelectInput
      label="Competition categories"
      name={name || "competitionRouteGroups"}
      options={
        competition?.routeGroups?.map((x) => ({
          name: x.name,
          value: x.uuid,
        })) || []
      }
    />
  );
};

/**
 * Select all of a competition's route groups when the user picks it.
 *
 * @param prefix - Optional prefix for the form field names.
 */
export const useUpdateRouteGroupsWhenCompetitionChanges = (prefix?: string) => {
  const withPrefix = (s: string) => (prefix ? `${prefix}.${s}` : s);
  const { setValue } = useFormContext();
  const venueUuid = useCurrentVenueUuid();

  useOnUserChange<string | null>(withPrefix("competition"), (competitionUuid) => {
    if (!competitionUuid) {
      return;
    }
    const competition = getCachedCompetition(competitionUuid, venueUuid);
    setValue(
      withPrefix("competitionRouteGroups"),
      competition?.routeGroups?.map((x) => x.uuid) ?? [],
    );
  });
};

export const useTakenNumbers = ({
  circuitUuid,
  competitionRouteGroupUuids,
  currentNumber,
}: {
  circuitUuid: string | undefined;
  competitionRouteGroupUuids: string[] | undefined;
  currentNumber?: number | null;
}) => {
  const { data: routes } = useVenueRoutesQuery();
  const takenNumbers = useMemo(() => {
    if (!routes || !circuitUuid || !competitionRouteGroupUuids) {
      return [];
    }

    let filterFn: (route: Route) => boolean = () => false;
    if (circuitUuid) {
      filterFn = (route) => route.circuit?.uuid === circuitUuid;
    } else if (competitionRouteGroupUuids) {
      filterFn = (route) =>
        route.competitionRouteGroups.some((crg) => competitionRouteGroupUuids.includes(crg.uuid));
    }

    return [
      ...new Set(
        routes
          ?.filter(filterFn)
          .map((route) => route.circuitNumber)
          .filter((x) => !!x && x !== currentNumber),
      ),
    ] as number[];
  }, [routes, circuitUuid, competitionRouteGroupUuids, currentNumber]);

  return takenNumbers;
};

/**
 * Keep the route's line-dependent fields in step with its line. When the user
 * picks a line, its quickdraw count and height are copied into the form and
 * autobelay is set to match, all marked dirty because the bulk form only saves
 * dirty fields. Returns whether the line is an autobelay line, in which case
 * the form hides the autobelay checkbox.
 */
const useSyncFieldsFromLine = () => {
  const currentVenueUuid = useCurrentVenueUuid();
  const { setValue } = useFormContext();
  const linesByUuid = useVenueLinesByUuid(currentVenueUuid);

  useOnUserChange<string | null>("startLine", (lineUuid, prevLineUuid) => {
    const line = linesByUuid?.[lineUuid || ""];
    const prevLine = linesByUuid?.[prevLineUuid || ""];
    if (line) {
      setValue("quickdrawCount", line.quickdrawCount || 0, { shouldDirty: true });
      setValue("height", line.height || 0, { shouldDirty: true });
    }
    if (line?.autoBelay) {
      setValue("autoBelay", true, { shouldDirty: true });
    } else if (prevLine?.autoBelay) {
      setValue("autoBelay", false, { shouldDirty: true });
    }
  });

  // An autobelay line always means autobelay, including for a route's stored
  // values when it's first shown. That case isn't marked dirty.
  const lineUuid = useWatch({ name: "startLine" });
  const autoBelay = useWatch({ name: "autoBelay" });
  const lineIsAutoBelay = !!linesByUuid?.[lineUuid || ""]?.autoBelay;
  useEffect(() => {
    if (lineIsAutoBelay && !autoBelay) {
      setValue("autoBelay", true);
    }
  }, [lineIsAutoBelay, autoBelay, setValue]);

  return { lineIsAutoBelay };
};

/**
 * The route number picker. Most routes don't need a number, so with no circuit
 * chosen it starts collapsed behind an "Add number" link, unless the route
 * already has one. Once opened it can be collapsed again by clicking the label.
 * A circuit that shows numbers on its pins always opens it and can't be
 * collapsed, and one that shows grades or nothing never does.
 */
export const RouteNumberField = ({
  route,
  routeDisplay,
  takenNumbers,
  name = "circuit.number",
}: {
  route?: Partial<Route>;
  routeDisplay: string | undefined;
  takenNumbers: number[];
  name?: string;
}) => {
  const [expanded, setExpanded] = useState(!!route?.circuitNumber);

  if (routeDisplay !== "NUMBER" && routeDisplay !== undefined) {
    return null;
  }

  return (
    <FormSection>
      {routeDisplay === "NUMBER" || expanded ? (
        <NumberSelectInput
          label="Number"
          name={name}
          initialCount={Math.max(30, route?.circuitNumber ? route.circuitNumber : 0)}
          takenNumbers={takenNumbers}
          onCollapse={routeDisplay === "NUMBER" ? undefined : () => setExpanded(false)}
        />
      ) : (
        <FormGroup>
          <FormLabel onClick={() => setExpanded(true)} sx={{ cursor: "pointer" }}>
            Number
            <KeyboardArrowDown />
          </FormLabel>
        </FormGroup>
      )}
    </FormSection>
  );
};

export const RouteFormSingle = ({ route }: { route?: Partial<Route> & { uuid: string } }) => {
  const { data: venue } = useCurrentVenueQuery();
  const { data: zones } = useVenueZonesByClimbType(venue?.uuid, "BOULDER");

  const circuitUuid = useWatch({ name: "circuit.uuid" });
  const circuit = useCurrentVenueCircuitLookup(circuitUuid || undefined);

  const competitionRouteGroupUuids = useWatch({
    name: "competitionRouteGroups",
  });

  const takenNumbers = useTakenNumbers({
    circuitUuid,
    competitionRouteGroupUuids,
    currentNumber: route?.circuitNumber,
  });

  const competitionUuid = useWatch({ name: "competition" });

  // When the competition field is changed by the user,
  // automatically select all the comp route groups.
  useUpdateRouteGroupsWhenCompetitionChanges();

  const { lineIsAutoBelay } = useSyncFieldsFromLine();

  const competition = useCurrentVenueCompetitionLookup(competitionUuid);

  return (
    <>
      {route?.climbType === "SPORT" ? (
        <>
          <FormSection>
            <LineSelect route={route} withCompetitions />
          </FormSection>
          <FormSection>
            <TagScanInput />
          </FormSection>
          {competitionUuid ? (
            <FormSection>
              <CompetitionCategoriesSelect competitionUuid={competitionUuid} />
            </FormSection>
          ) : null}
        </>
      ) : (
        <>
          <FormSection>
            <CircuitSelect
              withCompetitions
              canDetachFromCompetition={competition?.status !== "ENDED"}
              disabledDueToActiveCompetition={
                !!competition?.entriesCount && !["ENDED", "UPCOMING"].includes(competition?.status)
              }
            />
          </FormSection>
          {competitionUuid ? (
            <FormSection>
              <CompetitionCategoriesSelect competitionUuid={competitionUuid} />
            </FormSection>
          ) : null}
          <FormSection>
            <TagScanInput />
          </FormSection>
          <RouteNumberField
            key={route?.uuid}
            route={route}
            routeDisplay={circuit?.routeDisplay}
            takenNumbers={takenNumbers}
          />
          {circuit?.gradesEstimated ? (
            <FormSection>
              <DifficultyInput />
            </FormSection>
          ) : null}
        </>
      )}
      {route?.climbType === "BOULDER" && zones && zones.length > 0 ? (
        <FormSection>
          <PillsSelectInput
            label="Zone"
            name="zone"
            options={
              zones?.map((zone) => ({
                name: zone.name,
                value: zone.uuid,
              })) || []
            }
            emptyWarning="Zone is missing"
          />
        </FormSection>
      ) : null}
      <FormSection>
        <GradePickerFormInput label="Grade" name="gradeValue" />
      </FormSection>
      <FormSection>
        <Stack direction={"row"} spacing={1}>
          <ColorsFormInput label="Hold colors" name="colors" />
        </Stack>
      </FormSection>
      <FormSection>
        <TraitSelectorFormInput label="Styles" traitType="styles" name="styles" />
      </FormSection>
      <FormSection>
        <TraitSelectorFormInput label="Skills" traitType="skills" name="skills" />
      </FormSection>
      <FormSection>
        <TraitSelectorFormInput label="Holds" traitType="holds" name="holds" />
      </FormSection>
      <FormSection>
        <RouteLabelsInput label="Labels" name="labels" />
      </FormSection>
      {route?.climbType === "SPORT" ? (
        <>
          {lineIsAutoBelay ? null : (
            <FormSection>
              <CheckboxInput name="autoBelay" label="Has autobelay?" />
            </FormSection>
          )}
          <FormSection>
            <NumberInput name="quickdrawCount" label="Quickdraws" />
          </FormSection>
          <FormSection>
            <HeightInputAutoUnits name="height" label="Height" />
          </FormSection>
          {competitionUuid ? (
            <>
              <FormSection>
                <NumberInput name="segmentsCount" label="Number of holds" />
              </FormSection>
              <FormSection>
                <MapPositionEditInput route={route} />
              </FormSection>
            </>
          ) : null}
        </>
      ) : (
        <FormSection>
          <MapPositionEditInput route={route} />
        </FormSection>
      )}
      <FormSection>
        <TextInput name="name" label="Name" />
      </FormSection>
      <FormSection>
        <TextareaInput name="description" label="Description" />
      </FormSection>
      <FormSection>
        <StaffSelect label="Setters" name="setters" onlyActiveStaff multiple />
      </FormSection>
      <FormSection>
        <DateInput
          name="scheduledEndTime"
          label="Scheduled end date"
          withTime={false}
          defaultTime="12:00:00"
          asDateTime
          minDate={format(new Date(), "yyyy-MM-dd")}
        />
      </FormSection>
    </>
  );
};

export const FormButtons = ({ disabled }: { disabled?: boolean }) => {
  const createRouteData = useRoutesUiStore((s) => s.createRouteData);
  const formState = useFormState<RouteFormValues>();

  let saveText = "Save";
  if (formState.isSubmitting) {
    saveText = "Saving...";
  }
  if (formState.isDirty || createRouteData) {
    return (
      <Stack
        sx={{
          position: "sticky",
          bottom: "0px",
          padding: 1,
          zIndex: 10,
          backgroundColor: "white",
        }}
        direction="row"
        spacing={1}
      >
        <Button type="reset" disabled={formState.isSubmitting} color="neutral" variant="soft">
          Clear
        </Button>
        <Button
          sx={{ flex: 1 }}
          type="submit"
          disabled={disabled}
          loading={formState.isSubmitting}
          loadingPosition="start"
        >
          {saveText}
        </Button>
      </Stack>
    );
  }
  return null;
};

export const RouteFormMultiple = ({ routes }: { routes: Route[] }) => {
  const { data: venue } = useCurrentVenueQuery();
  const { data: zones } = useVenueZonesByClimbType(venue?.uuid, "BOULDER");
  const competitionUuid = useWatch({ name: "competition" });

  useUpdateRouteGroupsWhenCompetitionChanges();
  const { lineIsAutoBelay } = useSyncFieldsFromLine();

  const routeCompetitionUuids = [
    ...new Set(routes.map((r) => r.competitionRouteGroups?.[0]?.competition)),
  ];
  const multipleCompetitions = routeCompetitionUuids.length > 1;
  const existingCompetition = useCurrentVenueCompetitionLookup(routeCompetitionUuids[0]);

  return (
    <>
      {routes[0]?.climbType === "SPORT" ? (
        <FormSection>
          <LineSelect />
        </FormSection>
      ) : (
        <>
          <FormSection>
            <CircuitSelect
              name="circuit"
              withCompetitions
              canDetachFromCompetition={
                !multipleCompetitions && existingCompetition?.status !== "ENDED"
              }
              disabledDueToActiveCompetition={
                !!existingCompetition?.entriesCount &&
                !["ENDED", "UPCOMING"].includes(existingCompetition?.status)
              }
            />
          </FormSection>
          {competitionUuid ? (
            <FormSection>
              <CompetitionCategoriesSelect
                competitionUuid={competitionUuid}
                name="competitionRouteGroups"
              />
            </FormSection>
          ) : null}
        </>
      )}
      {routes[0]?.climbType === "BOULDER" && zones && zones.length > 0 ? (
        <FormSection>
          <PillsSelectInput
            label="Zone"
            name="zone"
            options={
              zones?.map((zone) => ({
                name: zone.name,
                value: zone.uuid,
              })) || []
            }
          />
        </FormSection>
      ) : null}
      <FormSection>
        <GradePickerFormInput label="Grade" name="gradeValue" />
      </FormSection>
      <FormSection>
        <Stack direction={"row"} spacing={1}>
          <ColorsFormInput label="Hold colors" name="colors" />
        </Stack>
      </FormSection>
      <FormSection>
        <TraitSelectorFormInput label="Styles" traitType="styles" name="styles" />
      </FormSection>
      <FormSection>
        <TraitSelectorFormInput label="Skills" traitType="skills" name="skills" />
      </FormSection>
      <FormSection>
        <TraitSelectorFormInput label="Holds" traitType="holds" name="holds" />
      </FormSection>
      {routes[0]?.climbType === "SPORT" ? (
        <>
          {lineIsAutoBelay ? null : (
            <FormSection>
              <CheckboxInput name="autoBelay" label="Has autobelay?" />
            </FormSection>
          )}
          <FormSection>
            <NumberInput name="quickdrawCount" label="Quickdraws" />
          </FormSection>
          <FormSection>
            <HeightInputAutoUnits name="height" label="Height" />
          </FormSection>
        </>
      ) : null}
      <FormSection>
        <StaffSelect label="Setters" name="setters" onlyActiveStaff multiple />
      </FormSection>
      <FormSection>
        <DateInput name="scheduledEndTime" label="Scheduled end date" />
      </FormSection>
    </>
  );
};
